// Donations View page
import React, { useState, useEffect } from "react";
import { Button } from "../atoms";
import { AdminLayout } from "../organisms";
import { donationService } from "../../services/donationService";

export const DonationsView = () => {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState({
    from: "",
    to: "",
  });
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    loadDonations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDonations = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await donationService.getDonations();
      setDonations(data);
      calculateTotal(data);
    } catch (err) {
      setError("Failed to load donations");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async () => {
    if (!filter.from && !filter.to) {
      loadDonations();
      return;
    }

    try {
      setLoading(true);
      setError("");
      const data = await donationService.getDonationsByDateRange(
        filter.from,
        filter.to,
      );
      setDonations(data);
      calculateTotal(data);
    } catch (err) {
      setError("Failed to filter donations");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = (data) => {
    const total = data.reduce((sum, donation) => sum + donation.amount, 0);
    setTotalAmount(total);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <AdminLayout title="Donations Management">
      {/* Summary Card */}
      <div className="bg-white rounded-2xl shadow-soft border border-terracotta-100/60 p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-ink-soft text-sm font-semibold uppercase tracking-wider">
              Total Amount
            </h3>
            <p className="text-3xl font-bold text-terracotta-700">
              £{totalAmount.toFixed(2)}
            </p>
            <p className="text-ink-soft text-sm mt-1">
              {donations.length} donations
            </p>
          </div>
          <Button
            variant="primary"
            className="!px-3 !py-1.5 !text-sm"
            onClick={loadDonations}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-2xl shadow-soft border border-terracotta-100/60 p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Filter by Date Range</h3>
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium mb-2">From Date</label>
            <input
              type="date"
              value={filter.from}
              onChange={(e) => setFilter({ ...filter, from: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-terracotta-500"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium mb-2">To Date</label>
            <input
              type="date"
              value={filter.to}
              onChange={(e) => setFilter({ ...filter, to: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-terracotta-500"
            />
          </div>
          <div>
            <Button
              variant="primary"
              className="!px-3 !py-1.5 !text-sm"
              onClick={handleFilter}
            >
              Apply Filter
            </Button>
          </div>
          <div>
            <Button
              variant="secondary"
              className="!px-3 !py-1.5 !text-sm"
              onClick={() => {
                setFilter({ from: "", to: "" });
                loadDonations();
              }}
            >
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Donations Table */}
      <div className="bg-white rounded-2xl shadow-soft border border-terracotta-100/60 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-ink-soft">
            Loading donations...
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">{error}</div>
        ) : donations.length === 0 ? (
          <div className="p-8 text-center text-ink-soft">
            No donations found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-terracotta-100/40">
                {donations.map((donation) => (
                  <tr
                    key={donation.id}
                    className="hover:bg-cream/50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink">
                      {donation.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink">
                      {donation.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-soft">
                      {donation.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-terracotta-700">
                      £{donation.amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-soft">
                      {formatDate(donation.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
